import type { ClassNames } from 'react-day-picker/types/ClassNames';

import { css } from 'emotion';
import type { DatePickerAlignment } from './alignment';

import { breakpoints, colors, fonts, icons } from '../../styles';

const outside = css`
  background-color: ${colors.white} !important;
`;

const daySize = '32px';

export const pickerSurroundingPadding = 20;

export const basePicker = css`
  margin-top: 30px;
`;

export const filterBasePicker = css`
  margin-top: 0px;
`;

const navButton = css`
  position: absolute;
  cursor: pointer;
  width: ${icons.size.medium};
  height: ${icons.size.medium};

  ::before {
    content: "";
    position: absolute;
    width: 0;
    height: 0;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    border-top: 5px solid transparent;
    border-bottom: 5px solid transparent;
    border-left: 7px solid ${colors.gray.dark};
  }
`;

export const emptyClassNames: ClassNames = {
  container: '',
  wrapper: '',
  interactionDisabled: '',
  navBar: '',
  navButtonPrev: '',
  navButtonNext: '',
  navButtonInteractionDisabled: '',

  months: '',
  month: '',
  caption: '',
  weekdays: '',
  weekdaysRow: '',
  weekday: '',
  body: '',
  week: '',
  day: '',

  today: '',
  selected: '',
  disabled: '',
  outside: '',
  todayButton: '',
  footer: '',
  weekNumber: '',
};

export const classNames: ClassNames = {
  ...emptyClassNames,
  day: css`
    width: ${daySize};
    height: ${daySize};
    display: flex;
    font-size: ${fonts.size.small};
    font-weight: ${fonts.weight.regular};
    color: ${colors.gray.dark};
    align-items: center;
    justify-content: center;
    outline: none;
    cursor: pointer;
  `,

  weekdaysRow: css`
    display: flex;
  `,

  week: css`
    display: flex;
  `,

  weekday: css`
    width: ${daySize};
    height: ${daySize};
    display: flex;
    font-size: ${fonts.size.smallest};
    align-items: center;
    justify-content: center;
    color: ${colors.gray.dark};
    font-weight: ${fonts.weight.regular};

    abbr[title] {
      text-decoration: none;
      cursor: auto;
      border-bottom: none;
    }
  `,
  outside,
  months: css`
    justify-content: space-between;
  `,
  month: css`
    div[role="heading"] {
      display: flex;
      justify-content: center;
      align-items: center;
      margin-bottom: 20px;
      margin-top: 1.5px;

      div {
        font-size: ${fonts.size.medium};
        color: ${colors.black};
        font-weight: ${fonts.weight.medium};
      }
    }
  `,
  navButtonPrev: css`
    ${navButton} transform: rotate(180deg);
    left: 0;
  `,
  navButtonNext: css`
    ${navButton}
    right: 0;
  `,
  wrapper: css`
    position: relative;
    outline: none;
    & > div {
      display: flex;
      margin-top: -2px;
    }
  `,
  today: css`
    color: ${colors.primary.default};
    font-weight: ${fonts.weight.bold} !important;
  `,
  disabled: css`
    color: ${colors.custom.gray50};
    pointer-events: none;
    background-color: ${colors.gray.lightest};
  `,
};

export const input = css`
  width: 100%;
  border: 1px solid ${colors.gray.light};
  box-sizing: border-box;
`;

export const navbar = css`
  display: flex;
  align-items: center;

  @media screen and (max-width: ${breakpoints.tablet}) {
    flex-wrap: wrap;
  }
`;

export const navbarWrapper = css`
  width: 100%;
  box-sizing: border-box;
`;

export const navbarLogo = css`
  margin: 0 18px;
  min-width: ${icons.size.smallest};
  min-height: ${icons.size.smallest};
  color: ${colors.gray.light};
`;

export const singleNavbarWrapper = css`
  ${navbarWrapper};
`;

export const filterIcon = css`
  height: ${icons.size.small};
  margin-right: 10px;

  & > svg {
    font-size: ${icons.size.small};
  }
`;

export const multiNavbarWrapper = css`
  ${navbarWrapper};
`;

export const wrapper = css`
  box-shadow: 2px 2px 10px 0 ${colors.shadows.default};
  border: 1px solid ${colors.gray.light};
  padding: ${pickerSurroundingPadding}px;
  box-sizing: border-box;
  position: relative;
`;

export const pickerContainer = (isDayPickerOpen?: boolean, hasErrorOnFirstInput?: boolean) => css`
  max-height: 90px;
  width: 100%;
  position: relative;

  ${
    !isDayPickerOpen &&
    hasErrorOnFirstInput &&
    `
    @media screen and (max-width: ${breakpoints.tablet}) {
      max-height: 105px;
    }
  `
  };
`;

export const dateFilterContainer = css`
  max-height: 90px;
  position: relative;
`;

export const singleDatePicker = {
  classNames: {
    ...classNames,
    selected: css`
      border-radius: 50%;
      background-color: ${colors.custom.primary10};
      color: ${colors.gray.dark};
    `,
  },
  modifierStyles: {
    current: {
      borderRadius: '50%',
      backgroundColor: colors.primary.default,
      color: colors.white,
    },
  },
  wrapper: (isDayPickerOpen: boolean) => css`
    width: 264px;
    ${wrapper};
    ${isDayPickerOpen && `background-color: ${colors.white};`};
  `,
};

export const inputError = css`
  &&& {
    border: 1px solid ${colors.error.default};
  }
`;

export const inputErrorLabel = css`
  display: block;
  color: ${colors.error.default};
  font-size: ${fonts.size.smallest};
  line-height: 17px;
  position: absolute;
  top: 100%;
  margin-top: 5px;
  white-space: nowrap;
`;

export const inputErrorWrapper = (isDayPickerOpen?: boolean, hasErrorOnFirstInput?: boolean) => css`
  position: relative;
  width: 100%;

  ${
    isDayPickerOpen &&
    `
    @media screen and (max-width: ${breakpoints.tablet}) {
      width: 176px;

      :not(:first-of-type) {
        margin-top: ${hasErrorOnFirstInput ? '25' : '10'}px;
      }
    }
  `
  };
`;

export const inputWrapper = css`
  flex-grow: 1;
  max-width: 230px;
  min-width: 120px;
  position: relative;
`;

export const removeInputFocusStyles = css`
  && {
    border: 1px solid ${colors.gray.light};
    box-shadow: none;
  }
`;

export const filterBorder = css`
  border-radius: 5px;
`;

export const pickerPosition = (topPosition: number, rightPosition: number, leftPosition: number, alignment: DatePickerAlignment = 'right') => css`
  && {
    position: fixed;
    z-index: 1;
    top: ${topPosition - pickerSurroundingPadding}px;
    ${alignment === 'left' ? `left: ${leftPosition - pickerSurroundingPadding}px` : `right: ${rightPosition - pickerSurroundingPadding}px;`};
  }
`;

export const multiDatePicker = {
  classNames: {
    ...classNames,
    selected: css`
      &:not(.${outside}) {
        background-color: ${colors.primary.default};
        color: ${colors.white};
      }
      &:not(.start):not(.end):not(.${outside}) {
        border-radius: 0;
        background-color: ${colors.custom.accessory};
        color: ${colors.gray.dark};
      }
    `,
  },
  modifierStyles: {
    start: {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
      backgroundColor: colors.primary.default,
      color: colors.white,
    },
    end: {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
      backgroundColor: colors.primary.default,
      color: colors.white,
    },
  },
  wrapper: css`
    width: 538px;
    ${wrapper};
  `,
};

export const chipsStyles = {
  chipContainer: css`
    display: flex;
    margin-top: 20px;

    & > *:not(:last-child) {
      margin-right: 15px;
    }
  `,
};
